import React from "react";
import { Box, CircularProgress, SxProps } from "@mui/material";
import { customColors } from "src/theme";

interface CustomButtonProps {
  mode?: "primary" | "secondary";
  text: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  sx?: SxProps;
  type?: "submit" | "reset" | "button" | undefined;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  mode = "primary",
  text,
  icon,
  isLoading = false,
  isDisabled = false,
  onClick,
  sx = {},
  type,
}) => {
  const color = customColors.gold;
  const fontColor = customColors.textAccent;
  const isPrimary = mode === "primary";
  return (
    <Box
      component="button"
      type={type}
      onClick={onClick}
      disabled={isDisabled || isLoading}
      className="fancy-button"
      sx={{
        position: "relative",
        cursor: "pointer",
        padding: "10px 24px",
        fontSize: "16px",
        color: isPrimary ? fontColor : color,
        border: `2px solid ${color} `,
        borderRadius: "12px",
        backgroundColor: isPrimary ? customColors.gold : "transparent",
        fontWeight: isPrimary ? 500 : 700,
        overflow: "hidden",
        transition: "all 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
        transform: "scale(1)",

        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          margin: "auto",
          width: "50px",
          height: "50px",
          borderRadius: "inherit",
          transform: "scale(0)",
          zIndex: -1,
        },

        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: "0 0px 20px rgba(193, 163, 98, 0.3)",
        },

        "&:hover::before": {
          transform: "scale(12)",
        },

        "&:active": {
          transform: "scale(1)",
        },

        "&:disabled": {
          opacity: 0.6,
          pointerEvents: "none",
        },

        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: icon && !isLoading ? "0.5em" : 0,
          whiteSpace: "nowrap",
        }}
      >
        {!isLoading && icon}
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: "#212121" }} />
        ) : (
          text
        )}
      </Box>
    </Box>
  );
};
